import React from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Button,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import TableChartIcon from '@mui/icons-material/TableChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import FolderIcon from '@mui/icons-material/Folder';
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import TitleIcon from '@mui/icons-material/Title';
import NumbersIcon from '@mui/icons-material/Numbers';
import { DISPLAY_LABELS } from '../../utils/widgetModel';

// The builder is a STRUCTURAL editor: it lays cards out and says what each one
// will show, without executing anything. So a card here is a labelled
// placeholder — display kind, bound entry, and its function — not a live render.
// The live render is the job of ViewDisplay.

const DISPLAY_ICONS = {
  tabs: ViewCompactIcon,
  table: TableChartIcon,
  stat: NumbersIcon,
  chart: BarChartIcon,
  file: InsertDriveFileIcon,
  shelf: FolderIcon,
  header: TitleIcon,
};

function ViewFunctionCard({
  widgetId,
  widget,
  functionName,
  entryLabel,
  missing = false,
  onRemove,
  onConfigure,
}) {
  if (missing) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderColor: 'error.main',
        }}
      >
        <Alert severity="error" sx={{ mb: 1 }}>
          This card&apos;s function was deleted
        </Alert>
        <Button size="small" color="error" onClick={() => onRemove(widgetId)}>
          Remove card
        </Button>
      </Paper>
    );
  }

  const display = widget?.display || 'tabs';
  const Icon = DISPLAY_ICONS[display] || ViewCompactIcon;
  const title = widget?.title
    || (display === 'stat' ? widget?.config?.label : null)
    || (display === 'header' ? (widget?.config?.text || 'Header') : null)
    || entryLabel
    || functionName
    || 'Card';

  return (
    <Paper
      elevation={2}
      sx={{
        p: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 1,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* The drag handle is the grip and title ONLY. react-grid-layout binds
          mousedown on whatever matches its draggableHandle selector and
          swallows it, so a button inside the handle would never fire its
          click — the buttons therefore sit outside the handle element. */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 0.5,
          p: 0.5,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box
          className="drag-handle"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flex: 1,
            minWidth: 0,
            cursor: 'move',
          }}
        >
          <DragIndicatorIcon fontSize="small" sx={{ opacity: 0.6, flexShrink: 0 }} />
          <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
            {title}
          </Typography>
        </Box>
        {onConfigure && (
          <Tooltip title={display === 'header' ? 'Edit header' : 'Card display'} placement="top">
            <IconButton size="small" onClick={() => onConfigure(widgetId)}>
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Remove" placement="top">
          <IconButton
            size="small"
            onClick={() => onRemove(widgetId)}
            sx={{ '&:hover': { color: 'error.main' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 0.75,
          p: 1,
          bgcolor: 'background.default',
          overflow: 'hidden',
        }}
      >
        <Icon sx={{ color: 'text.disabled' }} />
        <Chip size="small" variant="outlined" label={DISPLAY_LABELS[display] || display} />
        {display !== 'header' && functionName && (
          <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '100%' }}>
            {functionName}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

export default ViewFunctionCard;
